
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Project } from "../../entities/Project";
import { ProjectLanguage } from "../../entities/ProjectLanguage";
import ImportService, { ImportEntry, ImportPlan, ImportSummary } from "../../services/ImportService";
import ImportDataCache from "../../services/ImportDataCache";
import { findLanguageCodeForFileName, getFlagCode, getLanguageLabel } from "../../utils/languageUtils";
import LanguageDropdown from "../../components/LanguageDropdown.vue";
import { toastSuccess, toastError } from "../../utils/toast";

type RowStatus = "ready" | "importing" | "done" | "failed";

interface ImportRow {
  id: number;
  file: File;
  languageCode: string;
  // frozen once read, so Vue doesn't make thousands of entries reactive
  entries: readonly ImportEntry[] | null;
  parseError: string;
  plan: Readonly<ImportPlan> | null;
  previewLoading: boolean;
  previewError: string;
  status: RowStatus;
  summary: ImportSummary | null;
  importError: string;
  showErrors: boolean;
}

@Component({ components: { LanguageDropdown } })
export default class ImportTab extends Vue {
  @Prop({ required: true }) project!: Project;

  @Prop({ required: true }) languages!: ProjectLanguage[];

  rows: ImportRow[] = [];

  nextRowId = 1;

  dragOver = false;

  fileError = "";

  importing = false;

  finished = false;

  showFormat = false;

  importService = new ImportService();

  dataCache: ImportDataCache | null = null;

  created() {
    this.dataCache = new ImportDataCache(this.project.id, this.importService);
  }

  /** The tab is reused when switching to another project, so start over with that project's data. */
  @Watch("project.id")
  onProjectChanged() {
    this.dataCache = new ImportDataCache(this.project.id, this.importService);
    this.rows = [];
    this.finished = false;
    this.fileError = "";
  }

  get cache(): ImportDataCache {
    return this.dataCache as ImportDataCache;
  }

  /** Updates a row in place (assigning to a parameter's property directly isn't allowed by the lint rules). */
  patchRow(row: ImportRow, patch: Partial<ImportRow>) {
    Object.assign(row, patch);
  }

  get languageItems(): { text: string; value: string }[] {
    return this.languages.map((l) => ({
      text: getLanguageLabel(l.languageCode),
      value: l.languageCode,
    }));
  }

  /** Language codes chosen for more than one file */
  get duplicateLanguageCodes(): Set<string> {
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    this.rows.forEach((row) => {
      if (!row.languageCode) return;
      if (seen.has(row.languageCode)) duplicates.add(row.languageCode);
      seen.add(row.languageCode);
    });
    return duplicates;
  }

  get allPreviewsReady(): boolean {
    return this.rows.every((row) => !!row.plan && !row.previewLoading);
  }

  get canImport(): boolean {
    return this.rows.length > 0 && !this.importing && this.allPreviewsReady && this.duplicateLanguageCodes.size === 0;
  }

  /** Totals across all previews. A new key in several files is counted once, since its term is only created once. */
  get previewTotals() {
    const newTermKeys = new Set<string>();
    const totals = { newTerms: 0, newTranslations: 0, changed: 0, unchanged: 0, skipped: 0 };
    this.rows.forEach(({ plan }) => {
      if (!plan) return;
      plan.newTerms.forEach((entry) => newTermKeys.add(entry.key));
      totals.newTranslations += plan.newTranslations.length;
      totals.changed += plan.changedTranslations.length;
      totals.unchanged += plan.unchangedCount;
      totals.skipped += plan.conflicts.length;
    });
    totals.newTerms = newTermKeys.size;
    return totals;
  }

  get totals() {
    const totals = { termsCreated: 0, translationsCreated: 0, translationsUpdated: 0, translationsUnchanged: 0, errors: 0 };
    this.rows.forEach(({ summary, importError }) => {
      if (importError) totals.errors += 1;
      if (!summary) return;
      totals.termsCreated += summary.termsCreated;
      totals.translationsCreated += summary.translationsCreated;
      totals.translationsUpdated += summary.translationsUpdated;
      totals.translationsUnchanged += summary.translationsUnchanged;
      totals.errors += summary.errors.length;
    });
    return totals;
  }

  languageName(code: string): string {
    return getLanguageLabel(code);
  }

  flagCode(code: string): string {
    return getFlagCode(code);
  }

  /** The line under each file: its error, or what importing it would change */
  rowMessage(row: ImportRow): { text: string; isError: boolean } {
    if (row.parseError) return { text: row.parseError, isError: true };
    if (!row.entries) return { text: "Reading file…", isError: false };
    if (!row.languageCode) return { text: "Select a language to see what will change.", isError: false };
    if (this.duplicateLanguageCodes.has(row.languageCode)) return { text: "Another file is set to this language.", isError: true };
    if (row.previewError) return { text: row.previewError, isError: true };
    if (row.previewLoading || !row.plan) return { text: "Calculating changes…", isError: false };

    const { plan } = row;
    const skipped = plan.conflicts.length ? ` · ${plan.conflicts.length} skipped (plural mismatch)` : "";
    const changes = plan.newTerms.length + plan.newTranslations.length + plan.changedTranslations.length;
    if (changes === 0) {
      return { text: `No changes — all ${plan.unchangedCount} translations are already up to date.${skipped}`, isError: !!skipped };
    }
    const parts = [
      `${plan.newTerms.length} new ${plan.newTerms.length === 1 ? "term" : "terms"}`,
      `${plan.newTranslations.length} new ${plan.newTranslations.length === 1 ? "translation" : "translations"}`,
      `${plan.changedTranslations.length} updated`,
      `${plan.unchangedCount} unchanged`,
    ];
    return { text: `${parts.join(" · ")}${skipped}`, isError: !!skipped };
  }

  statusIcon(row: ImportRow): string {
    if (row.status === "done") return row.summary?.errors.length ? "mdi-alert-circle" : "mdi-check-circle";
    if (row.status === "failed") return "mdi-close-circle";
    return "mdi-file-document-outline";
  }

  statusColor(row: ImportRow): string {
    if (row.status === "done") return row.summary?.errors.length ? "warning" : "success";
    if (row.status === "failed") return "error";
    return "#9CA3AF";
  }

  triggerFileInput() {
    if (this.importing) return;
    (this.$refs.fileInput as HTMLInputElement).click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.addFiles(Array.from(input.files ?? []));
    // allow choosing the same file again
    input.value = "";
  }

  onFileDrop(event: DragEvent) {
    this.dragOver = false;
    if (this.importing) return;
    this.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  addFiles(files: File[]) {
    this.fileError = "";
    const skipped = files.filter((file) => !file.name.toLowerCase().endsWith(".json")).map((file) => file.name);
    if (skipped.length > 0) this.fileError = `Only .json files can be imported. Skipped: ${skipped.join(", ")}`;

    const languageCodes = this.languageItems.map((l) => l.value);
    files
      .filter((file) => file.name.toLowerCase().endsWith(".json"))
      .forEach((file) => {
        // a file with the same name replaces the previous one
        this.rows = this.rows.filter((row) => row.file.name !== file.name);
        const row: ImportRow = {
          id: this.nextRowId,
          file,
          languageCode: findLanguageCodeForFileName(file.name, languageCodes) ?? "",
          entries: null,
          parseError: "",
          plan: null,
          previewLoading: false,
          previewError: "",
          status: "ready",
          summary: null,
          importError: "",
          showErrors: false,
        };
        this.nextRowId += 1;
        this.rows.push(row);
        this.readFile(row);
      });
  }

  async readFile(row: ImportRow) {
    try {
      const text = await row.file.text();
      this.patchRow(row, { entries: Object.freeze(this.importService.parseEntries(text)) });
      await this.refreshPreview(row);
    } catch (e: any) {
      this.patchRow(row, { parseError: e.message || "Could not read the file." });
    }
  }

  /** Works out what importing the file would change. Uses the cached project data, so it's cheap to call again. */
  async refreshPreview(row: ImportRow) {
    this.patchRow(row, { plan: null, previewError: "" });
    if (!row.entries || !row.languageCode) return;

    const code = row.languageCode;
    this.patchRow(row, { previewLoading: true });
    try {
      const [terms, translations] = await Promise.all([this.cache.getTerms(), this.cache.getTranslations(code)]);
      // ignore the result if the language was changed in the meantime
      if (row.languageCode !== code) return;
      this.patchRow(row, { plan: Object.freeze(this.importService.buildPlan(code, row.entries, terms, translations)) });
    } catch (e: any) {
      if (row.languageCode === code) this.patchRow(row, { previewError: e.message || "Could not load the project's data." });
    } finally {
      if (row.languageCode === code) this.patchRow(row, { previewLoading: false });
    }
  }

  removeRow(row: ImportRow) {
    this.rows = this.rows.filter((r) => r.id !== row.id);
  }

  /**
   * Imports the files one after another. Each plan is rebuilt right before its import from the in-memory data,
   * which the previous imports keep up to date, so a term created by one file isn't created again by the next.
   */
  async runImport() {
    if (!this.canImport) return;
    this.importing = true;

    // eslint-disable-next-line no-restricted-syntax
    for (const row of this.rows) {
      this.patchRow(row, { status: "importing" });
      try {
        // eslint-disable-next-line no-await-in-loop
        const [terms, translations] = await Promise.all([this.cache.getTerms(), this.cache.getTranslations(row.languageCode)]);
        const plan = this.importService.buildPlan(row.languageCode, row.entries as readonly ImportEntry[], terms, translations);
        // eslint-disable-next-line no-await-in-loop
        const summary = await this.importService.execute(this.project.id, plan, terms, translations);
        this.patchRow(row, { summary, status: "done", showErrors: summary.errors.length > 0 && this.rows.length === 1 });
        // a failed write may mean the in-memory data is out of date (e.g. changed in another tab), so reload it for the
        // next file. Skipped entries (plural mismatches) are reported as errors too, but nothing was written for them.
        if (summary.errors.length > plan.conflicts.length) this.cache.clear();
      } catch (e: any) {
        this.patchRow(row, { status: "failed", importError: e.message || "Import failed." });
        // the in-memory data may no longer match the database
        this.cache.clear();
      }
    }

    this.importing = false;
    this.finished = true;

    const { termsCreated, translationsCreated, translationsUpdated, errors } = this.totals;
    if (errors > 0) {
      toastError(`Import completed with ${errors} ${errors === 1 ? "error" : "errors"}.`);
    } else {
      toastSuccess("Import completed.");
    }
    if (termsCreated > 0) this.$emit("terms-changed");
    if (translationsCreated > 0 || translationsUpdated > 0) this.$emit("translations-updated");
  }

  resetImport() {
    this.rows = [];
    this.finished = false;
    this.fileError = "";
    // start the next batch from fresh data
    this.cache.clear();
  }
}
